import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useKeycloak } from '../components/KeycloakProvider';
import { CreateIssueRequest, CreateTimeLogRequest, Issue, IssuesListResponse, TimeLog, TimeLogsListResponse, UpdateTimeLogRequest, User } from '../types';
import { ActiveTimer, useApiClient } from '../hooks';
import { createIssueRequestSchema, createTimeLogRequestSchema, formatDateISO, getErrorMessage, updateTimeLogRequestSchema, uuidSchema, validateOrThrow } from '../utils';
import { useNotifications } from './NotificationContext';
import { useTimer } from './TimerContext';

interface FetchIssuesParams {
  pageSize?: number;
  pageToken?: string;
  filter?: string;
  orderBy?: string;
  labels?: string[];
}

interface FetchIssuesResult {
  items: Issue[];
  nextPageToken?: string | null;
  totalSize?: number | null;
}

interface TimeTrackerContextType {
  currentUser: User | null;
  currentUserLoading: boolean;
  issues: Issue[];
  issuesLoading: boolean;
  issuesError: string | null;
  issuesTotalSize: number | null;
  activeTimer: ActiveTimer | null;
  todayTimeLogs: TimeLog[];
  todayTimeLogsLoading: boolean;
  refreshIssues: () => Promise<void>;
  fetchIssues: (params: FetchIssuesParams) => Promise<FetchIssuesResult>;
  refreshTodayTimeLogs: () => Promise<void>;
  startTimer: (issue: Issue) => void;
  stopTimer: () => Promise<void>;
  createIssue: (summary: string, startDateTime?: Date, endDateTime?: Date) => Promise<Issue>;
  createWorkLog: (issueId: string, issueKey: string, startDateTime: Date, endDateTime: Date, labels?: string[]) => Promise<void>;
  updateTimeLog: (id: string, startedTime: string, timeSpentSeconds: number, labels?: string[]) => Promise<void>;
  deleteTimeLog: (id: string) => Promise<void>;
}

const TimeTrackerContext = createContext<TimeTrackerContextType | null>(null);

export const useTimeTracker = (): TimeTrackerContextType => {
  const context = useContext(TimeTrackerContext);
  if (!context) {
    throw new Error('useTimeTracker must be used within a TimeTrackerProvider');
  }
  return context;
};

interface TimeTrackerProviderProps {
  children: ReactNode;
}

export const TimeTrackerProvider: React.FC<TimeTrackerProviderProps> = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const apiClient = useApiClient();
  const { activeTimer, startTimer, clearTimer } = useTimer();
  const { notifyError, notifySuccess } = useNotifications();

  // Current user state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentUserLoading, setCurrentUserLoading] = useState(true);

  // Issues state
  const [issues, setIssues] = useState<Issue[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState<string | null>(null);
  const [issuesTotalSize, setIssuesTotalSize] = useState<number | null>(null);

  // Today's time logs state
  const [todayTimeLogs, setTodayTimeLogs] = useState<TimeLog[]>([]);
  const [todayTimeLogsLoading, setTodayTimeLogsLoading] = useState(false);

  // Cache for issues (to avoid refetching for time logs)
  const issuesCacheRef = useRef<Map<string, Issue>>(new Map());

  // Prevent double fetch in StrictMode
  const userFetchedRef = useRef(false);

  // Fetch current user on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!initialized || !keycloak?.authenticated) {
        setCurrentUserLoading(false);
        return;
      }

      // Skip if already fetched (StrictMode protection)
      if (userFetchedRef.current) {
        return;
      }
      userFetchedRef.current = true;

      try {
        const user = await apiClient.get<User>('/users/me');
        setCurrentUser(user);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
        notifyError('Failed to load user', 'Could not fetch user data');
        // Reset flag on error so it can retry
        userFetchedRef.current = false;
      } finally {
        setCurrentUserLoading(false);
      }
    };

    fetchCurrentUser();
  }, [apiClient, initialized, keycloak?.authenticated, notifyError]);

  const fetchIssues = useCallback(async (params: FetchIssuesParams): Promise<FetchIssuesResult> => {
    if (!initialized || !keycloak?.authenticated) {
      return { items: [], nextPageToken: null, totalSize: null };
    }

    const queryParams = new URLSearchParams();
    if (params.pageSize) {
      queryParams.set('pageSize', String(params.pageSize));
    }
    if (params.pageToken) {
      queryParams.set('pageToken', params.pageToken);
    }
    if (params.filter) {
      queryParams.set('filter', params.filter);
    }
    if (params.orderBy) {
      queryParams.set('orderBy', params.orderBy);
    }
    if (params.labels && params.labels.length > 0) {
      queryParams.set('labels', params.labels.join(','));
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/issues?${queryString}` : '/issues';

    const response = await apiClient.get<IssuesListResponse>(url);

    // Update issues cache
    response.items.forEach((issue) => {
      issuesCacheRef.current.set(issue.id, issue);
    });

    return {
      items: response.items,
      nextPageToken: response.nextPageToken,
      totalSize: response.totalSize,
    };
  }, [apiClient, initialized, keycloak?.authenticated]);

  const refreshIssues = useCallback(async () => {
    if (!initialized || !keycloak?.authenticated) {
      return;
    }

    setIssuesLoading(true);
    setIssuesError(null);
    try {
      const result = await fetchIssues({ pageSize: 20 });
      setIssues(result.items);
      setIssuesTotalSize(result.totalSize ?? null);
    } catch (error) {
      setIssuesError(getErrorMessage(error, 'Failed to load issues'));
    } finally {
      setIssuesLoading(false);
    }
  }, [fetchIssues, initialized, keycloak?.authenticated]);

  // Store latest deps in refs for stable refreshTodayTimeLogs callback
  const currentUserRef = useRef(currentUser);
  const notifyErrorRef = useRef(notifyError);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);
  useEffect(() => { notifyErrorRef.current = notifyError; }, [notifyError]);

  const refreshTodayTimeLogs = useCallback(async () => {
    if (!initialized || !keycloak?.authenticated) {
      return;
    }

    const userId = currentUserRef.current?.id;
    if (!userId) {
      return;
    }

    setTodayTimeLogsLoading(true);
    try {
      // Filter worklogs by current user for today only (server-side date filter)
      const today = new Date();
      const todayStart = `${formatDateISO(today)}T00:00:00Z`;
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStart = `${formatDateISO(tomorrow)}T00:00:00Z`;
      const escapedUserId = userId.replace(/"/g, '\\"');

      const workLogsResponse = await apiClient.get<TimeLogsListResponse>(
        `/workLogs?pageSize=100&filter=authorId="${escapedUserId}" AND startedAt>="${todayStart}" AND startedAt<"${tomorrowStart}"&orderBy=startedAt desc`
      );

      const todayWorkLogs = workLogsResponse.items;

      // Find issue IDs that are not in cache
      const missingIssueIds = new Set<string>();
      todayWorkLogs.forEach((log) => {
        if (!issuesCacheRef.current.has(log.issueId)) {
          missingIssueIds.add(log.issueId);
        }
      });

      // Fetch missing issues in a single batch request
      if (missingIssueIds.size > 0) {
        const idsArray = Array.from(missingIssueIds);
        const filterParts = idsArray.map((id) => `id="${id}"`).join(' OR ');
        const issuesResponse = await apiClient.get<IssuesListResponse>(
          `/issues?pageSize=100&filter=${encodeURIComponent(filterParts)}`
        );
        issuesResponse.items.forEach((issue) => {
          issuesCacheRef.current.set(issue.id, issue);
        });
      }

      const todayLogs = todayWorkLogs.map((log) => {
        const issue = issuesCacheRef.current.get(log.issueId);
        return {
          ...log,
          issueKey: issue?.key,
          issueSummary: issue?.summary,
          issueLabels: issue?.labels,
        };
      });

      todayLogs.sort((a, b) => new Date(b.startedTime).getTime() - new Date(a.startedTime).getTime());
      setTodayTimeLogs(todayLogs);
    } catch (error) {
      console.error('Failed to load today time logs:', error);
      notifyErrorRef.current('Failed to load logs', 'Could not fetch today\'s work logs');
    } finally {
      setTodayTimeLogsLoading(false);
    }
  }, [apiClient, initialized, keycloak?.authenticated]);

  // Trigger initial fetch when currentUser becomes available
  useEffect(() => {
    if (currentUser) {
      refreshTodayTimeLogs();
    }
  }, [currentUser, refreshTodayTimeLogs]);

  const createIssue = useCallback(async (summary: string, startDateTime?: Date, endDateTime?: Date): Promise<Issue> => {
    const keycloakUserId = keycloak?.tokenParsed?.sub;
    if (!keycloakUserId) {
      throw new Error('User not authenticated');
    }

    const hasEnd = !!endDateTime;
    const status = hasEnd ? 'Done' : 'In Progress';

    const request: CreateIssueRequest = {
      summary,
      status,
      assigneeId: keycloakUserId,
    };
    const validatedRequest = validateOrThrow(createIssueRequestSchema, request);

    try {
      const newIssue = await apiClient.post<Issue>('/issues', validatedRequest);

      // Add to cache
      issuesCacheRef.current.set(newIssue.id, newIssue);

      if (hasEnd && startDateTime) {
        // Create worklog with the given time range
        const timeSpentSeconds = Math.floor((endDateTime.getTime() - startDateTime.getTime()) / 1000);
        const timestamp = Math.floor(startDateTime.getTime() / 1000);
        const username = keycloak?.tokenParsed?.preferred_username || keycloakUserId;
        const worklogJiraId = `wl-${newIssue.key}-${username}-${timestamp}`;

        const worklogRequest: CreateTimeLogRequest = {
          jiraId: worklogJiraId,
          issueId: newIssue.id,
          authorId: keycloakUserId,
          startedTime: startDateTime.toISOString(),
          timeSpentSeconds,
        };
        const validatedWorklog = validateOrThrow(createTimeLogRequestSchema, worklogRequest);
        await apiClient.post<TimeLog>('/workLogs', validatedWorklog);
        await refreshTodayTimeLogs();
        notifySuccess('Issue created', `${newIssue.key}: time logged successfully`);
      } else {
        // Start timer on the new issue (stop existing timer first)
        if (activeTimer) {
          try {
            await stopTimerRef.current();
          } catch (err) {
            console.error('Failed to stop previous timer:', err);
          }
        }
        startTimer(newIssue);
        notifySuccess('Issue created', `${newIssue.key}: timer started`);
      }

      return newIssue;
    } catch (error) {
      console.error('Failed to create issue:', error);
      notifyError('Create failed', getErrorMessage(error, 'Could not create issue'));
      throw error;
    }
  }, [apiClient, keycloak?.tokenParsed, activeTimer, startTimer, refreshTodayTimeLogs, notifySuccess, notifyError]);

  // Ref to allow createIssue to call the latest stopTimer without circular dependency
  const stopTimerRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const stopTimer = useCallback(async () => {
    if (!activeTimer) return;

    const keycloakUserId = keycloak?.tokenParsed?.sub;
    if (!keycloakUserId) {
      throw new Error('User not authenticated');
    }

    const endTime = new Date();
    const timeSpentSeconds = Math.floor((endTime.getTime() - activeTimer.startTime.getTime()) / 1000);

    const timestamp = Math.floor(activeTimer.startTime.getTime() / 1000);
    const username = keycloak?.tokenParsed?.preferred_username || keycloakUserId;
    const worklogJiraId = `wl-${activeTimer.issueKey}-${username}-${timestamp}`;

    try {
      const request: CreateTimeLogRequest = {
        jiraId: worklogJiraId,
        issueId: activeTimer.issueId,
        authorId: keycloakUserId,
        startedTime: activeTimer.startTime.toISOString(),
        timeSpentSeconds,
      };
      // Validate request before sending to API
      const validatedRequest = validateOrThrow(createTimeLogRequestSchema, request);
      await apiClient.post<TimeLog>('/workLogs', validatedRequest);
      clearTimer();
      notifySuccess('Time saved', `Work log for ${activeTimer.issueKey} has been saved`);
      await refreshTodayTimeLogs();
    } catch (error) {
      console.error('Failed to save time log:', error);
      notifyError('Save failed', 'Could not save work log');
      throw error;
    }
  }, [activeTimer, apiClient, keycloak?.tokenParsed, clearTimer, refreshTodayTimeLogs, notifySuccess, notifyError]);

  // Keep stopTimerRef in sync with latest stopTimer
  useEffect(() => {
    stopTimerRef.current = stopTimer;
  }, [stopTimer]);

  const updateTimeLog = useCallback(
    async (id: string, startedTime: string, timeSpentSeconds: number, labels?: string[]) => {
      // Validate ID format
      validateOrThrow(uuidSchema, id);
      // Validate update request
      const updateRequest = { startedTime, timeSpentSeconds, ...(labels !== undefined && { labels }) };
      const validatedRequest = validateOrThrow(updateTimeLogRequestSchema, updateRequest);

      try {
        await apiClient.patch<TimeLog>(`/workLogs/${id}`, validatedRequest as UpdateTimeLogRequest);
        notifySuccess('Updated', 'Work log has been updated');
        // Invalidate issue cache if labels changed so fresh data is fetched
        if (labels !== undefined) {
          const log = todayTimeLogs.find((l) => l.id === id);
          if (log) issuesCacheRef.current.delete(log.issueId);
        }
        await refreshTodayTimeLogs();
      } catch (error) {
        console.error('Failed to update time log:', error);
        notifyError('Update failed', 'Could not update work log');
        throw error;
      }
    },
    [apiClient, todayTimeLogs, refreshTodayTimeLogs, notifySuccess, notifyError]
  );

  const deleteTimeLog = useCallback(
    async (id: string) => {
      // Validate ID format
      validateOrThrow(uuidSchema, id);

      try {
        await apiClient.delete(`/workLogs/${id}`);
        notifySuccess('Deleted', 'Work log has been deleted');
        await refreshTodayTimeLogs();
      } catch (error) {
        console.error('Failed to delete time log:', error);
        notifyError('Delete failed', 'Could not delete work log');
        throw error;
      }
    },
    [apiClient, refreshTodayTimeLogs, notifySuccess, notifyError]
  );

  const createWorkLog = useCallback(
    async (issueId: string, issueKey: string, startDateTime: Date, endDateTime: Date, labels?: string[]) => {
      const keycloakUserId = keycloak?.tokenParsed?.sub;
      if (!keycloakUserId) {
        throw new Error('User not authenticated');
      }

      const timeSpentSeconds = Math.floor((endDateTime.getTime() - startDateTime.getTime()) / 1000);
      const timestamp = Math.floor(startDateTime.getTime() / 1000);
      const username = keycloak?.tokenParsed?.preferred_username || keycloakUserId;
      const worklogJiraId = `wl-${issueKey}-${username}-${timestamp}`;

      const request: CreateTimeLogRequest = {
        jiraId: worklogJiraId,
        issueId,
        authorId: keycloakUserId,
        startedTime: startDateTime.toISOString(),
        timeSpentSeconds,
        ...(labels && labels.length > 0 && { labels }),
      };

      try {
        const validatedRequest = validateOrThrow(createTimeLogRequestSchema, request);
        await apiClient.post<TimeLog>('/workLogs', validatedRequest);
        notifySuccess('Time logged', `Work log for ${issueKey} has been saved`);
        // Invalidate issue cache so labels are refreshed
        issuesCacheRef.current.delete(issueId);
        await refreshTodayTimeLogs();
      } catch (error) {
        console.error('Failed to create work log:', error);
        notifyError('Log failed', getErrorMessage(error, 'Could not create work log'));
        throw error;
      }
    },
    [apiClient, keycloak?.tokenParsed, refreshTodayTimeLogs, notifySuccess, notifyError]
  );

  const value = useMemo<TimeTrackerContextType>(() => ({
    currentUser,
    currentUserLoading,
    issues,
    issuesLoading,
    issuesError,
    issuesTotalSize,
    activeTimer,
    todayTimeLogs,
    todayTimeLogsLoading,
    refreshIssues,
    fetchIssues,
    refreshTodayTimeLogs,
    createIssue,
    createWorkLog,
    startTimer,
    stopTimer,
    updateTimeLog,
    deleteTimeLog,
  }), [currentUser, currentUserLoading, issues, issuesLoading, issuesError,
    issuesTotalSize, activeTimer, todayTimeLogs, todayTimeLogsLoading,
    refreshIssues, fetchIssues, refreshTodayTimeLogs, createIssue, createWorkLog,
    startTimer, stopTimer, updateTimeLog, deleteTimeLog]);

  return <TimeTrackerContext.Provider value={value}>{children}</TimeTrackerContext.Provider>;
};
